import React from "react";
import { useForm } from "react-hook-form";
import {
  Button,
  ModalFooter,
  Text,
  Flex,
  Input,
  Checkbox,
} from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import useCheckEmail from "./useCheckEmail";
import { safeGet, safeSet } from "./utils/safeStorage";
import useEmbedMode from "./hooks/useEmbedMode";

export const EmailInputStage = ({
  onNext,
  apiUrl,
  newsletter,
  bypassLogin,
}) => {
  const { t } = useTranslation();
  const embed = useEmbedMode();
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    defaultValues: {
      keepSignedIn: safeGet("keepSignedIn") === "true",
      email: safeGet("email"),
    },
  });
  const { checkEmail, loading, error } = useCheckEmail(apiUrl);

  const onSubmit = async (data) => {
    try {
      const { exists: emailExists, isMarketingSignup } = await checkEmail(
        data.email
      );
      safeSet("email", data.email);
      safeSet("keepSignedIn", data.keepSignedIn);

      onNext(data.email, emailExists, isMarketingSignup);
    } catch (err) {
      console.error("Error checking email:", err);

      // The mailing-list flow renders this stage without a bypassLogin prop, so
      // calling it unconditionally threw a TypeError inside the submit handler
      // and the Next button just did nothing. useCheckEmail has already set its
      // error state by this point, so the visitor sees the failure either way.
      if (typeof bypassLogin === "function") {
        bypassLogin();
      }
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Flex direction="column" gap={4}>
        <Flex direction="row" gap={4}>
          {/* The input is a flex sibling and takes the remaining space, so
              without this the label shrinks to min-content and breaks across
              two lines — "E-" / "Mail:" in German. */}
          <Text mt={2} flexShrink={0} whiteSpace="nowrap">
            {t("label.Email")}
            {":"}
          </Text>
          <Input
            // Autofocus inside an iframe pulls the host page down to the form on
            // load and takes focus off whatever the visitor was using. The
            // Modal's own autoFocus prop does not cover this — that governs the
            // modal's initial focus target, while this is a DOM attribute that
            // fires regardless.
            autoFocus={!embed}
            placeholder="example@email.com"
            // The domain part needs a dot and a top-level domain of two or more
            // characters. The old pattern /^\S+@\S+$/i accepted "name@gmailcom"
            // without a dot, so a missing dot passed as a valid address.
            {...register("email", {
              required: true,
              pattern: /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/i,
            })}
          />
        </Flex>
        {errors.email && (
          <Text color="red.500">{t("label.invalidEmailFormat")}</Text>
        )}
        {error && <Text color="red.500">{error}</Text>}
        {!newsletter && (
          <Checkbox {...register("keepSignedIn")}>
            {t("label.keepSignedIn")}
          </Checkbox>
        )}
      </Flex>
      <ModalFooter>
        <Button colorScheme="green" type="submit" isLoading={loading}>
          {t("button.Next")}
        </Button>
      </ModalFooter>
    </form>
  );
};
